import * as React from 'react';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import formatDate from 'date-fns/format';
import { Translate } from '../../../shared/LanguageService';
import { readValidationMessages, ValidationMessage } from '../../../shared/validationErrors';
import { useUserContext } from '../../../shared/context/user-context';
import { useTelemetryContext } from '../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../telemetry/utils';
import FailureSnackbar from '../../../shared/feedback/FailureSnackbar';
import WarningSnackbar from '../../../shared/feedback/WarningSnackbar';
import { CustomerAgreementCustomer, FirmsKeepingOwnAgreement } from '../../customerAgreements/types';
import { firmsKeepingOwnAgreementText } from './firmsKeepingOwnAgreementText';
import { useCustomerAgreementAssignmentQuery } from '../../customerAgreements/queries/useCustomerAgreementAssignmentQuery';
import { useCustomerAgreementsQuery } from '../../customerAgreements/queries/useCustomerAgreementsQuery';
import { useAssignCustomerAgreementMutation } from '../../customerAgreements/queries/useAssignCustomerAgreementMutation';

type CustomerAgreementTabProps = {
    customer: CustomerAgreementCustomer;
    canAssign: boolean;
};

export type CustomerAgreementTabHandle = {
    save: () => Promise<boolean>;
};

const noneOptionByKind = {
    'broker-firm': 'brokers/customer-agreement/option/none-for-firm',
    'broker-chain': 'brokers/customer-agreement/option/none-for-chain'
};

function failureText(messages: ValidationMessage[]): string {
    return messages.length > 0
        ? messages.map((message) => Translate(message.key)).join(' ')
        : Translate('brokers/customer-agreement/notification/assign-failed');
}

function selectValue(customerAgreementId: number | null): string {
    return customerAgreementId === null ? '' : String(customerAgreementId);
}

export const CustomerAgreementTab = React.forwardRef<
    CustomerAgreementTabHandle,
    CustomerAgreementTabProps
>(function CustomerAgreementTab({ customer, canAssign }, ref) {
    const { user } = useUserContext();
    const { logger } = useTelemetryContext();
    const assignmentQuery = useCustomerAgreementAssignmentQuery(customer);
    const agreementsQuery = useCustomerAgreementsQuery({ enabled: canAssign });
    const assignCustomerAgreement = useAssignCustomerAgreementMutation();
    const [chosenValue, setChosenValue] = React.useState<string | null>(null);
    const [failure, setFailure] = React.useState<ValidationMessage[] | null>(null);
    const [firmsKeepingOwnAgreement, setFirmsKeepingOwnAgreement] =
        React.useState<FirmsKeepingOwnAgreement | null>(null);

    const assignment = assignmentQuery.data;
    const loadFailed = assignmentQuery.isError || (canAssign && agreementsQuery.isError);
    const noneOption = Translate(noneOptionByKind[customer.kind]);
    const savedValue = assignment ? selectValue(assignment.customerAgreementId) : null;
    const shownValue = chosenValue !== null ? chosenValue : savedValue;

    const latest = React.useRef({
        chosenValue,
        savedValue,
        canAssign,
        customer,
        assignCustomerAgreement,
        logger,
        user
    });
    latest.current = {
        chosenValue,
        savedValue,
        canAssign,
        customer,
        assignCustomerAgreement,
        logger,
        user
    };

    const save = React.useCallback(async (): Promise<boolean> => {
        const current = latest.current;

        if (
            !current.canAssign ||
            current.chosenValue === null ||
            current.chosenValue === current.savedValue
        ) {
            return true;
        }

        try {
            const saved = await current.assignCustomerAgreement(
                current.customer,
                current.chosenValue === '' ? null : Number(current.chosenValue)
            );
            setChosenValue(null);
            setFirmsKeepingOwnAgreement(saved.firmsKeepingOwnAgreement || null);
            return true;
        } catch (error) {
            current.logger.logException(error, {
                origin: 'Brokers.AssignCustomerAgreement',
                userInfo: getUserInfo(current.user)
            });
            setFailure(await readValidationMessages(error));
            return false;
        }
    }, []);

    React.useImperativeHandle(ref, () => ({ save }), [save]);

    function renderContent() {
        if (loadFailed) {
            return (
                <Alert severity="error">{Translate('brokers/customer-agreement/load-error')}</Alert>
            );
        }

        if (!assignment || (canAssign && !agreementsQuery.data)) {
            return <Skeleton variant="rectangular" height={56} />;
        }

        if (!canAssign) {
            return (
                <Typography variant="body1">
                    {assignment.customerAgreementName || noneOption}
                </Typography>
            );
        }

        return (
            <TextField
                id={`customer-agreement-${customer.kind}-${customer.id}`}
                select
                SelectProps={{ native: true }}
                label={Translate('brokers/customer-agreement/field/agreement')}
                InputLabelProps={{ shrink: true }}
                value={shownValue}
                onChange={(event) => setChosenValue(event.target.value)}
                fullWidth
            >
                <option value="">{noneOption}</option>
                {agreementsQuery.data
                    .filter(
                        (agreement) =>
                            agreement.isActive || agreement.id === assignment.customerAgreementId
                    )
                    .map((agreement) => (
                        <option key={agreement.id} value={String(agreement.id)}>
                            {agreement.name}
                        </option>
                    ))}
            </TextField>
        );
    }

    return (
        <fieldset>
            <legend>{Translate('brokers/customer-agreement/heading')}</legend>
            <Stack spacing={1}>
                {renderContent()}
                {assignment && assignment.assignedAt ? (
                    <Typography variant="body2">
                        {Translate('brokers/customer-agreement/assigned-at', {
                            date: formatDate(new Date(assignment.assignedAt), 'yyyy-MM-dd')
                        })}
                    </Typography>
                ) : null}
                {assignment && assignment.copiedFromBrokerChainName ? (
                    <Typography variant="body2">
                        {Translate('brokers/customer-agreement/copied-from-chain', {
                            chain: assignment.copiedFromBrokerChainName
                        })}
                    </Typography>
                ) : null}
            </Stack>
            <FailureSnackbar
                open={failure !== null}
                onClose={() => setFailure(null)}
                text={failure !== null ? failureText(failure) : ''}
            />
            <WarningSnackbar
                open={firmsKeepingOwnAgreement !== null}
                onClose={() => setFirmsKeepingOwnAgreement(null)}
                stackIndex={1}
                text={
                    firmsKeepingOwnAgreement !== null
                        ? firmsKeepingOwnAgreementText(firmsKeepingOwnAgreement)
                        : ''
                }
            />
        </fieldset>
    );
});
